import { useCallback } from 'react';
import { useMutation, UseMutationOptions, UseMutationResult, MutationFunction } from 'react-query';
import ExceptionManager from '@/managers/exception-manager';

interface CustomMutationOptions<TData, TError, TVariables, TContext> extends Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn'> {
	shouldMutate?: boolean;
}

export function useMutationProvider<TData = unknown, TError = unknown, TVariables = void, TContext = unknown>(
	mutationFn: MutationFunction<TData, TVariables>,
	options?: CustomMutationOptions<TData, TError, TVariables, TContext>
): UseMutationResult<TData, TError, TVariables, TContext> & {
	safeMutate: (variables: TVariables) => Promise<TData | undefined>;
} {
	const updatedOptions: UseMutationOptions<TData, TError, TVariables, TContext> = {
		...options,
		onError: options?.onError
			? (error, variables, context) => {
					options?.onError?.(error, variables, context);
				}
			: (error, variables, context) => {
					ExceptionManager.errorHandler(error!);
					options?.onError?.(error, variables, context);
				}
	};

	const mutation = useMutation<TData, TError, TVariables, TContext>({
		mutationFn,
		...updatedOptions
	});

	const safeMutate = useCallback(
		async (variables: TVariables) => {
			if (options?.shouldMutate === false) {
				return undefined;
			}

			try {
				return await mutation.mutateAsync(variables);
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
			} catch (error) {
				// console.log(error);
				return undefined;
			}
		},
		[mutation, options?.shouldMutate]
	);

	return {
		...mutation,
		safeMutate
	};
}
